import { defineStore } from "pinia"
import { getBusinessRegionList, getRegionSimpleList } from '@/api/txn/region'
import { useUserStore } from "@/store/modules/user"
const userStore = useUserStore()
// 大区配置（Array 保证插入顺序，Object 的整数 key 会被 JS 自动数值排序）
const REGION_CONFIG = [
  { name: '西北', code: '15', regionIds: [610000, 620000, 650000, 630000, 640000] },
  { name: '华北', code: '11', regionIds: [110000, 120000, 130000, 370000, 140000] },
  { name: '华东', code: '12', regionIds: [320000, 330000, 310000, 340000, 350000] },
  { name: '华中', code: '13', regionIds: [420000, 410000, 430000, 360000] },
  { name: '华南', code: '17', regionIds: [440000, 450000, 520000, 530000, 460000] },
  { name: '东北', code: '14', regionIds: [220000, 230000, 210000, 150000] },
  { name: '西南', code: '16', regionIds: [500000, 510000, 540000] },
]

const EXCLUDE_REGIONS = [710000, 810000, 820000] // 台湾、香港、澳门

// ✅ 你要的风格：Pinia 组合式 + 持久化
export const useRegionStore = defineStore(
  "appRegion",
  () => {
    // 状态
    const regionList = ref<any[]>([])
    const groupedRegionList = ref<any[]>([])
    const currentRegion = ref<any>({})
    const loading = ref(false)
    // 分组处理
    const splitProvincesByRegion = (provinces: any[]) => {
      const regionMap: Record<string, any> = {}
      REGION_CONFIG.forEach((region: any) => {
        regionMap[region.code] = { ...region, provinces: [], enabled: true }
      })
      provinces.forEach((province) => {
        REGION_CONFIG.forEach((config: any) => {
          if (config.regionIds.includes(province.regionId)) {
            regionMap[config.code].provinces.push(province)
            if (!province.enabled) regionMap[config.code].enabled = false
          }
        })
      })

      // 按 REGION_CONFIG 中 regionIds 的顺序排序每个大区的省份，再用 REGION_CONFIG 顺序排序大区
      return REGION_CONFIG.map((config: any) => {
        const region = regionMap[config.code]
        const orderMap = new Map(
          config.regionIds.map((id: number, i: number) => [id, i]),
        )
        region.provinces.sort((a: any, b: any) => {
          const ai = orderMap.get(Number(a.regionId)) ?? 999
          const bi = orderMap.get(Number(b.regionId)) ?? 999
          return ai - bi
        })
        return region
      })
    }

    // 更新区域状态
    const updateGroupedRegionStatus = () => {
      groupedRegionList.value.forEach((region) => {
        region.enabled = region.provinces.every((p: any) => p.enabled)
      })
    }

    // 加载数据
    const loadRegionData = async () => {
      loading.value = true
      try {
        let res = [];
        // 是否加载商业区域:看 user 是否拥有至少一家已认证企业
        // 之前用 userStore.getEnte.authStatus(即第一家企业的 authStatus),多企业时若第一家未认证但其他已认证,会拿到错误的简化区域列表
        if (userStore.user.hasVerifiedCompany) {
          res = await getBusinessRegionList()
        } else {
          res = await getRegionSimpleList()
        }
        regionList.value = (res || []).map((item: any) => {
          let name = item.name
          // 新疆维吾尔自治区、宁夏回族自治区、西藏自治区、广西壮族自治区 只保留名字，例如新疆、宁夏、西藏、广西
          name = name.replace(/^(新疆|宁夏|西藏|广西)(维吾尔|回族|壮族)?自治区$/, '$1')
          return {
            id: item.id,
            regionId: item.regionId || item.id,
            regionName: name,
            code: item.code,
            enabled: item.enabled !== undefined ? item.enabled : true,
          }
        })
        groupedRegionList.value = splitProvincesByRegion(regionList.value)
        // 加载完成后 → 自动定位到当前省份
        if (regionList.value.length && !currentRegion.value?.regionId) {
          try {
            const AMAP_KEY = 'acfd31b136f87c2e9e4723de0af98c18'
            const url = `https://restapi.amap.com/v3/ip?key=${AMAP_KEY}`
            const res = await fetch(url)
            const resJson = await res.json()
            console.log('高德IP定位结果', resJson)

            let targetCode

            if (resJson.status === '1' && resJson.adcode) {
              const adcode = resJson.adcode.toString()
              const code = Number(adcode.slice(0, 2) + "0000")
              targetCode = code
            }
            const cRegion = regionList.value.find(item => item.regionId === targetCode)
            if (cRegion) {
              currentRegion.value = cRegion
            } else {
              currentRegion.value = regionList.value.find(item => item.regionId === 610000)
            }
          } catch (e) {
            currentRegion.value = regionList.value.find(item => item.regionId === 610000)
          }
        }
      } finally {
        loading.value = false
      }
    }

    // 设置当前区域（会自动持久化）
    const setCurrentRegion = (value: any) => {
      currentRegion.value = value
    }

    // 本地更新状态
    const updateProvinceStatusLocal = (provinceId: number, enabled: boolean) => {
      const p = regionList.value.find((i) => i.id === provinceId)
      if (p) p.enabled = enabled

      groupedRegionList.value.forEach((region) => {
        const item = region.provinces.find((i: any) => i.id === provinceId)
        if (item) item.enabled = enabled
      })
      updateGroupedRegionStatus()
    }

    // 获取开通省份
    const getOpenedProvinces = () => {
      return regionList.value
        .filter((i) => i.enabled && !EXCLUDE_REGIONS.includes(i.regionId))
        .map((p) => ({ label: p.regionName, value: p.regionId }))
    }

    // 过滤
    const filterByStatus = (status?: boolean) => {
      if (status === undefined) {
        groupedRegionList.value = splitProvincesByRegion(regionList.value)
      } else {
        const filtered = regionList.value.filter((i) => i.enabled === status)
        groupedRegionList.value = splitProvincesByRegion(filtered)
      }
      updateGroupedRegionStatus()
    }

    // 根据ID取名称
    const getNameById = (regionId: number) => {
      if (regionId === 0) return '全国'
      return regionList.value.find((i) => i.regionId === regionId)?.regionName
    }


    // 页面卸载时清理
    onUnmounted(() => { })

    return {
      // 状态
      groupedRegionList,// 分组后的区域列表
      currentRegion,// 当前选中的区域

      // 方法
      loadRegionData, //从后台获取区域数据
      setCurrentRegion,// 设置当前区域
      updateProvinceStatusLocal, // 更新省状态,打开关闭区域后，更新本地数据
      filterByStatus, // 根据状态过滤，区域配置里面的搜索
      getNameById,// 根据 ID 获取名称
      getOpenedProvinces, // 获取打开的省
    }
  },
  {
    persist: {
      paths: ["currentRegion", "regionList"]
    }
  }
)